import { Build, TrendingUp } from '@mui/icons-material';
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    LinearProgress,
    Typography,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Printer3dIcon from '../icons/Printer3dIcon';
import { getPrinterBrandBadgeProps } from '../../helpers/printerBrand';
import {
    buildPrinterOverviewJobsUrl,
    buildPrinterOverviewMaintenancesUrl,
} from '../../helpers/printersOverview';
import { formatFilamentGrams, formatProductionDurationMinutes } from '../../helpers/jobs';
import { liveUpdateHighlightSx } from '../../helpers/liveUpdateHighlight';
import MaintenanceDowntimeText from './MaintenanceDowntimeText';

const linkAreaSx = {
    borderRadius: 1,
    mx: -1,
    px: 1,
    py: 0.75,
};

export default function PrinterOverviewCard({ printer, period, highlighted = false }) {
    const navigate = useNavigate();
    const brandBadge = getPrinterBrandBadgeProps(printer.brand);
    const successRate = Number(printer.success_rate) || 0;
    const downtimeMinutes = Number(printer.maintenance_downtime_minutes) || 0;
    const jobsUrl = buildPrinterOverviewJobsUrl(printer.printer_uuid, period);
    const maintenancesUrl = buildPrinterOverviewMaintenancesUrl(printer.printer_uuid);

    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                borderColor: printer.is_printing ? 'success.main' : 'divider',
                boxShadow: printer.is_printing ? '0 0 0 1px rgba(46, 125, 50, 0.35)' : 'none',
                ...liveUpdateHighlightSx(highlighted),
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                        {brandBadge ? (
                            <Chip label={printer.name} size="small" {...brandBadge} />
                        ) : (
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                                {printer.name}
                            </Typography>
                        )}
                        {printer.model && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {printer.model}
                            </Typography>
                        )}
                    </Box>
                    <Chip
                        size="small"
                        icon={printer.is_printing ? <Printer3dIcon fontSize="small" /> : undefined}
                        label={printer.is_printing ? 'Imprimindo' : 'Parada'}
                        color={printer.is_printing ? 'success' : 'default'}
                        variant={printer.is_printing ? 'filled' : 'outlined'}
                    />
                </Box>

                <CardActionArea
                    onClick={() => navigate(jobsUrl)}
                    sx={linkAreaSx}
                    aria-label={`Ver impressões de ${printer.name}`}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <TrendingUp fontSize="small" color="primary" />
                        <Typography variant="body2" color="text.secondary">
                            Taxa de sucesso
                        </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                        {successRate.toFixed(1).replace('.', ',')}%
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            ({printer.total_jobs} impressões)
                        </Typography>
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(100, successRate)}
                        sx={{ height: 6, borderRadius: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.55 }}>
                        Tempo estimado:{' '}
                        <strong>{formatProductionDurationMinutes(printer.total_duration_minutes)}</strong>
                        {' · '}
                        Filamento: <strong>{formatFilamentGrams(printer.total_filament_grams)}</strong>
                    </Typography>
                </CardActionArea>

                <CardActionArea
                    onClick={() => navigate(maintenancesUrl)}
                    sx={{ ...linkAreaSx, mt: 1 }}
                    aria-label={`Ver manutenções de ${printer.name}`}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Build fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                            Manutenções:{' '}
                            <strong>{printer.maintenance_count}</strong>
                            {downtimeMinutes > 0 && (
                                <>
                                    {' '}
                                    (
                                    <MaintenanceDowntimeText totalMinutes={downtimeMinutes} />
                                    )
                                </>
                            )}
                        </Typography>
                    </Box>
                </CardActionArea>
            </CardContent>
        </Card>
    );
}
